import React, { useMemo, useState } from 'react';
import { MenuItem, TextField } from '@material-ui/core';
import { useFormContext, ValidationOptions } from 'react-hook-form';

import { CostCenterDisplay } from '../../components';
import { FormSelectProps } from '../../forms';
import { useFieldError } from '../../forms/form-methods';

export interface CostCenterInputProps extends Omit<FormSelectProps, 'children'> {
  costCenterOptions?: Array<{ value: string; label?: string }>;
}

export const CostCenterInput: React.FC<CostCenterInputProps> = ({
  name,
  defaultValue = '',
  costCenterOptions,
  validationOptions,
  size,
  disabled,
}) => {
  const { register, errors } = useFormContext();
  const fieldError = useFieldError(name, errors);

  const knownValues = useMemo(() => new Set(costCenterOptions?.map(o => o.value) ?? []), [
    costCenterOptions,
  ]);

  // If the stored value doesn't match any known option it was previously free-typed via "Other".
  const startAsOther = useMemo(() => !!defaultValue && !knownValues.has(defaultValue as string), [
    defaultValue,
    knownValues,
  ]);

  const initialSelectValue = startAsOther ? 'Other' : (defaultValue as string) ?? '';
  const initialOtherValue = startAsOther ? (defaultValue as string) : '';

  const [selectVal, setSelectVal] = useState(initialSelectValue);
  const [otherText, setOtherText] = useState(initialOtherValue);

  // React-controlled: drives the hidden input's element.value directly so
  // handleSubmit always reads the correct value via field.ref.value — no watch,
  // effects, or setValue needed.
  const [submittedValue, setSubmittedValue] = useState(
    startAsOther ? initialOtherValue : initialSelectValue
  );

  const isOther = selectVal === 'Other';

  return (
    <>
      <input
        type="hidden"
        name={name}
        ref={validationOptions ? register(validationOptions as ValidationOptions) : register}
        value={submittedValue}
        readOnly
      />
      <TextField
        select
        value={selectVal}
        fullWidth
        size={size}
        disabled={disabled}
        onChange={e => {
          const val = e.target.value;
          setSelectVal(val);
          setSubmittedValue(val === 'Other' ? '' : val);
        }}
        error={!isOther && !!fieldError}
        helperText={!isOther ? fieldError?.message : undefined}
        data-testid="costCenterSelect"
      >
        <MenuItem value="">None</MenuItem>
        {costCenterOptions?.map(option => (
          <MenuItem key={option.value} value={option.value}>
            <CostCenterDisplay value={option.value} />
          </MenuItem>
        ))}
        <MenuItem value="Other">Other</MenuItem>
      </TextField>
      {isOther && (
        <TextField
          label="Please specify"
          type="text"
          fullWidth
          size={size}
          disabled={disabled}
          value={otherText}
          onChange={e => {
            const text = e.target.value;
            setOtherText(text);
            setSubmittedValue(text);
          }}
          error={!!fieldError}
          helperText={fieldError?.message}
          data-testid="costCenterOtherInput"
        />
      )}
    </>
  );
};
